import { ArrowLeft } from "lucide-react";
import type { LabResultDetailsProps } from "../../types";
import { LabGraphHeader } from "../../molecules/LabGraphHeader";
import { LabTrendChart } from "../LabTrendChart/LabTrendChart";
import { LabPreviousReadingsList } from "../../molecules/LabPreviousReadingsList";
import { Button } from "../../../button";
import { useLabResultDetails } from "./useLabResultDetails";
import { cn } from "../../../../lib/utils";

export const LabResultDetails = ({
  result,
  onBack,
  className,
  ...props
}: LabResultDetailsProps & React.ComponentProps<"div">) => {
  const { effectiveReading, setSelectedReading, sortedReadings } = useLabResultDetails({ result });

  return (
    <div data-slot="lab-result-details" className={cn("flex flex-col h-full", className)} {...props}>
      {/* Fixed header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 dark:border-gray-800">
        <Button
          variant="outline"
          size="sm-icon"
          onClick={onBack}
          className="border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ArrowLeft size={15} />
        </Button>
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{result.name}</h2>
          <p className="text-xs text-gray-400 dark:text-gray-500">{result.category}</p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Graph header - selected reading stats */}
        <LabGraphHeader reading={effectiveReading} />

        {/* Graph */}
        <LabTrendChart result={result} selectedReadingId={effectiveReading.id} onSelectReading={setSelectedReading} />

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-800 mx-5" />

        {/* Previous readings */}
        <LabPreviousReadingsList
          selectedReadingId={effectiveReading.id}
          onSelectReading={setSelectedReading}
          sortedReadings={sortedReadings}
        />
      </div>
    </div>
  );
};
